// src/utils/permissions.js

export const PERMISSION_LEVELS = {
  EMPLOYEE: "employee",
  SCHEDULE_MANAGER: "schedule_manager",
  MANAGER: "manager",
  OWNER: "owner",
};

const PERMISSION_HIERARCHY = [
  PERMISSION_LEVELS.EMPLOYEE,
  PERMISSION_LEVELS.SCHEDULE_MANAGER,
  PERMISSION_LEVELS.MANAGER,
  PERMISSION_LEVELS.OWNER,
];

export function normalizePermissionLevel(permissionLevel) {
  const normalized = (permissionLevel || PERMISSION_LEVELS.EMPLOYEE).toString().toLowerCase();
  return PERMISSION_HIERARCHY.includes(normalized) ? normalized : PERMISSION_LEVELS.EMPLOYEE;
}

export function hasPermission(userPermission, requiredPermission) {
  const userLevel = PERMISSION_HIERARCHY.indexOf(normalizePermissionLevel(userPermission));
  const requiredLevel = PERMISSION_HIERARCHY.indexOf(normalizePermissionLevel(requiredPermission));
  return userLevel >= requiredLevel;
}

export function canAccessFeature(userPermission, feature) {
  const permission = normalizePermissionLevel(userPermission);

  const featurePermissions = {
    // Employee self-service
    employee_dashboard: [PERMISSION_LEVELS.EMPLOYEE],
    own_profile: [PERMISSION_LEVELS.EMPLOYEE],
    own_schedule: [PERMISSION_LEVELS.EMPLOYEE],

    // Admin portal capabilities
    dashboard_admin: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    view_all_schedules: [PERMISSION_LEVELS.SCHEDULE_MANAGER, PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    edit_schedules: [PERMISSION_LEVELS.SCHEDULE_MANAGER, PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    schedule_creation: [PERMISSION_LEVELS.SCHEDULE_MANAGER, PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    employee_availability: [PERMISSION_LEVELS.SCHEDULE_MANAGER, PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],

    // Employee Central
    employee_central: [PERMISSION_LEVELS.SCHEDULE_MANAGER, PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    employee_central_manage: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    employee_profile_personal: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    employee_assignments_manage: [PERMISSION_LEVELS.SCHEDULE_MANAGER, PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    employee_termination: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    can_invite_employee: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],

    // Financial/admin
    time_clock_admin: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    time_entries: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    payroll: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    wages_section: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    analytics: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    departments_manager: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    roles_manager: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    locations_manager: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    settings_central: [PERMISSION_LEVELS.OWNER],
    user_permissions: [PERMISSION_LEVELS.OWNER],
    delete_employees: [PERMISSION_LEVELS.OWNER],
    system_settings: [PERMISSION_LEVELS.OWNER],
    onboarding: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],

    // Backward compatibility aliases
    employee_management: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
    terminated_employees: [PERMISSION_LEVELS.MANAGER, PERMISSION_LEVELS.OWNER],
  };

  const allowedRoles = featurePermissions[feature];
  return !!allowedRoles && allowedRoles.includes(permission);
}

export function getPermissionInfo(permissionLevel) {
  const permission = normalizePermissionLevel(permissionLevel);

  const permissionInfo = {
    [PERMISSION_LEVELS.EMPLOYEE]: {
      label: "Employee",
      description: "Access to own dashboard only",
      color: "success",
      level: 1,
    },
    [PERMISSION_LEVELS.SCHEDULE_MANAGER]: {
      label: "Schedule Manager",
      description: "Can manage schedules and availability only",
      color: "info",
      level: 2,
    },
    [PERMISSION_LEVELS.MANAGER]: {
      label: "Manager",
      description: "Operational access, but cannot manage permissions or Settings Central",
      color: "warning",
      level: 3,
    },
    [PERMISSION_LEVELS.OWNER]: {
      label: "Owner",
      description: "100% access to all features and settings",
      color: "error",
      level: 4,
    },
  };

  return permissionInfo[permission] || permissionInfo[PERMISSION_LEVELS.EMPLOYEE];
}

export function canEditPermissions(editorPermission, targetPermission) {
  return normalizePermissionLevel(editorPermission) === PERMISSION_LEVELS.OWNER;
}

export function getAvailableNavigation(userPermission) {
  const allNavItems = [
    { path: "/dashboard", label: "Dashboard", feature: "dashboard_admin" },
    { path: "/employee-central", label: "Employee Central", feature: "employee_central" },
    { path: "/schedule-creation", label: "Create Schedule", feature: "schedule_creation" },
    { path: "/employee-availability", label: "Availability", feature: "employee_availability" },
    { path: "/time-entries", label: "Timesheets", feature: "time_entries" },
    { path: "/payroll", label: "Payroll", feature: "payroll" },
    { path: "/settings", label: "Settings", feature: "settings_central" },
  ];

  return allNavItems.filter((item) => canAccessFeature(userPermission, item.feature));
}
